import {
  findWordByNormalizedText,
  meaningsForWord,
} from "../../domain/vocabulary/grouping.ts";
import { isUsableWord, normalizeWordInput } from "../../domain/vocabulary/normalize.ts";
import type { WordType } from "../../domain/vocabulary/types.ts";
import {
  AI_CONTRACT_VERSION,
  type DiscoverMeaningsRequest,
  type MeaningCandidate,
  type MeaningPackage,
  type SelectedSense,
} from "../../contracts/ai/schemas.ts";
import { DISCOVER_CACHE_CANDIDATE_LIMIT } from "../../contracts/storage/discoverCache.ts";
import { isProductError, type ProductErrorCode } from "../../contracts/errors.ts";
import { createObservableStore } from "../observable.ts";
import type { VocabularyAi } from "../ports/ai.ts";
import type { DiscoverCacheStore } from "../ports/discoverCache.ts";
import type { Platform } from "../ports/platform.ts";
import type { VocabularyStore } from "../ports/storage.ts";
import { VocabularyCommands } from "../vocabulary/commands.ts";
import {
  comparisonIndexFromSnapshot,
  savedSenseDescriptors,
} from "../vocabulary/snapshotContext.ts";
import { buildReviewNote, validateMeaningPackage } from "./validatePackage.ts";

export interface AddWordInput {
  word: string;
  context?: string;
  definition?: string;
  russianHint?: string;
  typeHint?: WordType;
}

export interface DraftItem {
  itemId: string;
  candidate: MeaningCandidate;
  status: "queued" | "generating" | "ready" | "failed" | "conflict" | "removed";
  pack?: MeaningPackage;
  reviewNote?: string | null;
  error?: { code: ProductErrorCode | string; message: string };
  selectedForSave: boolean;
}

export interface GenerationDraft {
  draftId: string;
  requestId: string;
  input: AddWordInput;
  displayText: string;
  normalizedText: string;
  existingWordId?: string;
  baseRevision: number;
  candidates: MeaningCandidate[];
  selectedCandidateIds: string[];
  moreAvailable: boolean;
  corrections: string[];
  items: DraftItem[];
  reviewIndex: number;
}

export type GenerationPhase =
  | "idle"
  | "existing"
  | "discovering"
  | "choose"
  | "generating"
  | "review"
  | "saving"
  | "saved"
  | "error";

export interface GenerationState {
  phase: GenerationPhase;
  input: AddWordInput;
  existingWordId?: string;
  draft?: GenerationDraft;
  savedWordId?: string;
  savedMeaningIds?: string[];
  error?: { code: string; message: string };
  progressMessage?: string;
  lookingForAnother?: boolean;
}

const idle: GenerationState = { phase: "idle", input: { word: "" } };

export class GenerationController {
  private readonly store = createObservableStore<GenerationState>(idle);
  private abort?: AbortController;
  private generationToken = 0;
  private saveLock = false;

  constructor(
    private readonly vocabulary: VocabularyStore,
    private readonly ai: VocabularyAi,
    private readonly platform: Platform,
    private readonly commands: VocabularyCommands,
    private readonly discoverCache: DiscoverCacheStore,
    private readonly concurrency = 2,
  ) {}

  subscribe = this.store.subscribe.bind(this.store);
  getSnapshot = this.store.getSnapshot.bind(this.store);

  reset(): void {
    this.abort?.abort();
    this.saveLock = false;
    this.store.setState(idle);
  }

  setInput(input: Partial<AddWordInput>): void {
    this.store.setState((current) => ({
      ...current,
      input: { ...current.input, ...input },
    }));
  }

  inspectExisting(word: string): { exists: true; wordId: string } | { exists: false } {
    const snapshot = this.vocabulary.getSnapshot();
    if (!snapshot) {
      return { exists: false };
    }
    const normalized = normalizeWordInput(word);
    const existing = findWordByNormalizedText(snapshot, normalized.normalizedText);
    if (existing && meaningsForWord(snapshot, existing.id).length > 0) {
      return { exists: true, wordId: existing.id };
    }
    return { exists: false };
  }

  beginFromInput(): void {
    const input = this.store.getSnapshot().input;
    const normalized = normalizeWordInput(input.word);
    if (!isUsableWord(normalized)) {
      this.store.setState({
        phase: "error",
        input,
        error: { code: "validation_failed", message: "Enter an English word or phrase." },
      });
      return;
    }
    const existing = this.inspectExisting(normalized.displayText);
    if (existing.exists) {
      this.store.setState({
        phase: "existing",
        input: { ...input, word: normalized.displayText },
        existingWordId: existing.wordId,
      });
      return;
    }
    void this.discover({ ...input, word: normalized.displayText });
  }

  addAnotherMeaning(wordId: string): void {
    const snapshot = this.vocabulary.getSnapshot();
    const word = snapshot?.words.find((item) => item.id === wordId);
    if (!word) {
      this.store.setState({
        phase: "error",
        input: this.store.getSnapshot().input,
        error: { code: "not_found", message: "That word is no longer saved." },
      });
      return;
    }
    void this.discover(
      { ...this.store.getSnapshot().input, word: word.displayText },
      wordId,
    );
  }

  toggleCandidate(candidateId: string): void {
    const current = this.store.getSnapshot();
    if (!current.draft || current.phase !== "choose") {
      return;
    }
    const candidate = current.draft.candidates.find((item) => item.id === candidateId);
    if (!candidate || candidate.match.kind === "saved") {
      return;
    }
    const selected = current.draft.selectedCandidateIds.includes(candidateId)
      ? current.draft.selectedCandidateIds.filter((id) => id !== candidateId)
      : [...current.draft.selectedCandidateIds, candidateId];
    this.store.setState({
      ...current,
      draft: { ...current.draft, selectedCandidateIds: selected },
    });
  }

  setReviewIndex(index: number): void {
    const current = this.store.getSnapshot();
    if (!current.draft) {
      return;
    }
    const ready = current.draft.items.filter((item) => item.status === "ready");
    if (index < 0 || index >= ready.length) {
      return;
    }
    this.store.setState({
      ...current,
      draft: { ...current.draft, reviewIndex: index },
    });
  }

  removeItem(itemId: string): void {
    const current = this.store.getSnapshot();
    if (!current.draft) {
      return;
    }
    const items = current.draft.items.map((item) =>
      item.itemId === itemId ? { ...item, status: "removed" as const, selectedForSave: false } : item,
    );
    const remaining = items.filter((item) => item.status === "ready");
    this.store.setState({
      ...current,
      phase: remaining.length ? "review" : "choose",
      draft: {
        ...current.draft,
        items,
        selectedCandidateIds: current.draft.selectedCandidateIds.filter(
          (id) => items.find((item) => item.candidate.id === id)?.status !== "removed",
        ),
        reviewIndex: 0,
      },
    });
  }

  updateReadyPackage(itemId: string, pack: MeaningPackage): void {
    const current = this.store.getSnapshot();
    if (!current.draft) {
      return;
    }
    this.store.setState({
      ...current,
      draft: {
        ...current.draft,
        items: current.draft.items.map((item) =>
          item.itemId === itemId && item.status === "ready" ? { ...item, pack } : item,
        ),
      },
    });
  }

  async showMore(): Promise<void> {
    const current = this.store.getSnapshot();
    if (!current.draft) {
      return;
    }
    await this.discover(current.draft.input, current.draft.existingWordId, current.draft);
  }

  cancel(): void {
    this.abort?.abort();
    const current = this.store.getSnapshot();
    if (current.draft && (current.phase === "generating" || current.phase === "discovering")) {
      this.store.setState({
        ...current,
        phase: current.draft.candidates.length ? "choose" : "idle",
        progressMessage: undefined,
      });
    }
  }

  async continueWithSelection(): Promise<void> {
    const current = this.store.getSnapshot();
    if (!current.draft || current.draft.selectedCandidateIds.length === 0) {
      return;
    }
    await this.generateSelected(current.draft);
  }

  async retryItem(itemId: string): Promise<void> {
    const current = this.store.getSnapshot();
    if (!current.draft) {
      return;
    }
    const item = current.draft.items.find((entry) => entry.itemId === itemId);
    if (!item) {
      return;
    }
    await this.generateSelected({
      ...current.draft,
      items: current.draft.items.map((entry) =>
        entry.itemId === itemId ? { ...entry, status: "queued", error: undefined } : entry,
      ),
    });
  }

  async save(): Promise<void> {
    const current = this.store.getSnapshot();
    const draft = current.draft;
    if (!draft || this.saveLock || current.phase === "saving" || current.phase === "saved") {
      return;
    }
    if (current.phase !== "review") {
      return;
    }
    const ready = draft.items.filter((item) => item.status === "ready" && item.selectedForSave && item.pack);
    const failed = draft.items.filter(
      (item) => item.status === "failed" || item.status === "conflict" || item.status === "generating",
    );
    if (ready.length === 0) {
      this.store.setState({
        ...current,
        error: { code: "validation_failed", message: "Save at least one complete meaning." },
      });
      return;
    }
    if (failed.length > 0) {
      this.store.setState({
        ...current,
        error: {
          code: "validation_failed",
          message: `Repair or remove ${failed.length} unfinished ${failed.length === 1 ? "item" : "items"} before saving, or save only the ${ready.length} ready ${ready.length === 1 ? "meaning" : "meanings"} after removing the rest.`,
        },
      });
      return;
    }
    this.saveLock = true;
    this.store.setState({ ...current, phase: "saving", error: undefined });
    const snapshot = this.vocabulary.getSnapshot();
    if (!snapshot) {
      this.saveLock = false;
      this.store.setState({
        ...current,
        phase: "error",
        error: { code: "storage_unavailable", message: "Vocabulary is not available." },
      });
      return;
    }
    try {
      const result = await this.commands.savePackages({
        wordDisplay: draft.displayText,
        packages: ready.map((item) => item.pack!),
        existingWordId: draft.existingWordId,
        baseRevision: draft.baseRevision,
      });
      if (!result.ok) {
        this.store.setState({
          ...current,
          phase: "review",
          error: { code: result.code, message: result.message },
        });
        return;
      }
      this.store.setState({
        phase: "saved",
        input: draft.input,
        savedWordId: result.wordId,
        savedMeaningIds: result.meaningIds,
        draft,
      });
    } finally {
      this.saveLock = false;
    }
  }

  private async discover(
    input: AddWordInput,
    existingWordId?: string,
    previous?: GenerationDraft,
  ): Promise<void> {
    this.abort?.abort();
    this.abort = new AbortController();
    const token = ++this.generationToken;
    const requestId = this.platform.ids.create();
    const normalized = normalizeWordInput(input.word);
    const cacheable = !existingWordId && !hasRankingHints(input);

    if (cacheable) {
      const cached = await this.discoverCache.get(normalized.normalizedText);
      if (token !== this.generationToken) {
        return;
      }
      if (!previous && cached?.candidates.length) {
        await this.presentDiscovery({
          input,
          existingWordId,
          previous,
          requestId,
          candidates: cached.candidates,
          moreAvailable: cached.moreAvailable,
          corrections: cached.corrections,
        });
        return;
      }
      if (previous && cached) {
        const extra = cached.candidates.filter(
          (candidate) => !previous.candidates.some((item) => item.id === candidate.id),
        );
        if (extra.length) {
          await this.presentDiscovery({
            input,
            existingWordId,
            previous,
            requestId,
            candidates: extra,
            moreAvailable: cached.moreAvailable,
            corrections: cached.corrections,
          });
          return;
        }
        if (!cached.moreAvailable) {
          this.store.setState({
            phase: "choose",
            input,
            existingWordId,
            draft: { ...previous, moreAvailable: false },
          });
          return;
        }
      }
    }

    if (!this.platform.network.isOnline()) {
      this.store.setState({
        phase: "error",
        input,
        existingWordId,
        error: {
          code: "offline",
          message: "Finding meanings needs internet. Your saved words still work offline.",
        },
      });
      return;
    }

    this.store.setState({
      phase: "discovering",
      input,
      existingWordId,
      draft: previous,
      progressMessage: "Finding useful meanings…",
    });
    const snapshot = this.vocabulary.getSnapshot();
    const request: DiscoverMeaningsRequest = {
      contractVersion: AI_CONTRACT_VERSION,
      requestId,
      word: input.word,
      normalizedWord: normalized.normalizedText,
      context: input.context,
      definition: input.definition,
      russianHint: input.russianHint,
      typeHint: input.typeHint,
      existingSenses: savedSenseDescriptors(snapshot, existingWordId),
      previouslyShownCandidateIds: previous?.candidates.map((item) => item.id) ?? [],
    };
    try {
      const result = await this.ai.discoverMeanings(request, {
        signal: this.abort.signal,
        onProgress: (event) => {
          if (token !== this.generationToken) {
            return;
          }
          this.store.setState((current) => ({ ...current, progressMessage: event.message }));
        },
      });
      if (token !== this.generationToken) {
        return;
      }
      if (cacheable) {
        try {
          await this.rememberDiscovery(
            normalized.normalizedText,
            result.candidates,
            result.moreAvailable,
            result.corrections,
          );
        } catch {
          // Cache is optional; a full list still has to reach Choose Meanings.
        }
      }
      await this.presentDiscovery({
        input,
        existingWordId,
        previous,
        requestId,
        candidates: result.candidates,
        moreAvailable: result.moreAvailable,
        corrections: result.corrections,
      });
    } catch (error) {
      if (token !== this.generationToken) {
        return;
      }
      this.store.setState({
        phase: "error",
        input,
        existingWordId,
        draft: previous,
        error: this.describeError(error),
      });
    }
  }

  private async rememberDiscovery(
    normalizedWord: string,
    incoming: MeaningCandidate[],
    moreAvailable: boolean,
    corrections: string[],
  ): Promise<void> {
    const cached = await this.discoverCache.get(normalizedWord);
    const candidates = mergeCandidates(cached?.candidates ?? [], incoming).slice(0, DISCOVER_CACHE_CANDIDATE_LIMIT);
    if (!candidates.length) {
      return;
    }
    await this.discoverCache.put(normalizedWord, {
      candidates,
      moreAvailable,
      corrections: uniqueStrings([...(cached?.corrections ?? []), ...corrections]),
    });
  }

  private async presentDiscovery(input: {
    input: AddWordInput;
    existingWordId?: string;
    previous?: GenerationDraft;
    requestId: string;
    candidates: MeaningCandidate[];
    moreAvailable: boolean;
    corrections: string[];
  }): Promise<void> {
    const snapshot = this.vocabulary.getSnapshot();
    const merged = mergeCandidates(input.previous?.candidates ?? [], input.candidates).map((candidate) => {
      if (
        candidate.match.kind !== "new" &&
        !snapshot?.meanings.some(
          (meaning) => meaning.id === (candidate.match.kind === "new" ? "" : candidate.match.existingMeaningId),
        )
      ) {
        return { ...candidate, match: { kind: "new" as const } };
      }
      return candidate;
    });
    const suitable = merged.filter((candidate) => candidate.match.kind !== "saved");
    const draft: GenerationDraft = {
      draftId: input.previous?.draftId ?? this.platform.ids.create(),
      requestId: input.requestId,
      input: input.input,
      displayText: normalizeWordInput(input.input.word).displayText,
      normalizedText: normalizeWordInput(input.input.word).normalizedText,
      existingWordId: input.existingWordId,
      baseRevision: snapshot?.dataRevision ?? 0,
      candidates: merged,
      selectedCandidateIds: input.previous?.selectedCandidateIds ?? [],
      moreAvailable: input.moreAvailable,
      corrections: input.corrections,
      items: input.previous?.items ?? [],
      reviewIndex: 0,
    };
    if (suitable.length === 1 && draft.selectedCandidateIds.length === 0 && !input.previous) {
      draft.selectedCandidateIds = [suitable[0].id];
      this.store.setState({
        phase: "choose",
        input: input.input,
        existingWordId: input.existingWordId,
        draft,
        lookingForAnother: true,
      });
      await this.generateSelected(draft, true);
      return;
    }
    this.store.setState({
      phase: "choose",
      input: input.input,
      existingWordId: input.existingWordId,
      draft,
    });
  }

  private async generateSelected(draft: GenerationDraft, fromSingle = false): Promise<void> {
    if (!this.platform.network.isOnline()) {
      this.store.setState({
        phase: "error",
        input: draft.input,
        existingWordId: draft.existingWordId,
        draft,
        error: {
          code: "offline",
          message: "Creating learning cards needs internet. Saved meanings still work offline.",
        },
      });
      return;
    }
    this.abort?.abort();
    this.abort = new AbortController();
    const token = ++this.generationToken;
    const selected = draft.candidates.filter((candidate) =>
      draft.selectedCandidateIds.includes(candidate.id),
    );
    const items: DraftItem[] = selected.map((candidate) => {
      const existing = draft.items.find((item) => item.candidate.id === candidate.id);
      if (existing && existing.status === "ready") {
        return existing;
      }
      return {
        itemId: existing?.itemId ?? this.platform.ids.create(),
        candidate,
        status: "queued",
        selectedForSave: true,
      };
    });
    const working: GenerationDraft = { ...draft, items, reviewIndex: 0 };
    this.store.setState({
      phase: "generating",
      input: draft.input,
      existingWordId: draft.existingWordId,
      draft: working,
      lookingForAnother: fromSingle,
      progressMessage: `Creating ${items.filter((item) => item.status !== "ready").length} learning ${items.length === 1 ? "card" : "cards"}…`,
    });
    const pending = items.filter((item) => item.status !== "ready");
    await this.runPool(pending, this.concurrency, async (item) => {
      if (token !== this.generationToken) {
        return;
      }
      this.patchItem(item.itemId, { status: "generating" });
      const selectedSense: SelectedSense = {
        itemId: item.itemId,
        candidateId: item.candidate.id,
        type: item.candidate.type,
        shortLabel: item.candidate.shortLabel,
        definition: item.candidate.definition,
        example: item.candidate.example,
        sourceContext: draft.input.context,
        sourceDefinition: draft.input.definition,
        sourceHint: draft.input.russianHint,
        sourceType: draft.input.typeHint,
      };
      try {
        const result = await this.ai.generateMeaningPackages(
          {
            contractVersion: AI_CONTRACT_VERSION,
            requestId: this.platform.ids.create(),
            word: draft.displayText,
            normalizedWord: draft.normalizedText,
            senses: [selectedSense],
            comparisonIndex: comparisonIndexFromSnapshot(this.vocabulary.getSnapshot()),
            existingSenses: savedSenseDescriptors(this.vocabulary.getSnapshot(), draft.existingWordId),
          },
          {
            signal: this.abort?.signal,
            onProgress: (event) => {
              if (token !== this.generationToken) {
                return;
              }
              this.store.setState((current) => ({ ...current, progressMessage: event.message }));
            },
          },
        );
        if (token !== this.generationToken) {
          return;
        }
        const outcome = result.outcomes.find((entry) => entry.itemId === item.itemId) ?? result.outcomes[0];
        if (!outcome || outcome.status === "failed") {
          this.patchItem(item.itemId, {
            status: "failed",
            error: {
              code: outcome && outcome.status === "failed" ? outcome.code : "generation_failed",
              message:
                outcome && outcome.status === "failed"
                  ? outcome.message
                  : "Generation did not return this meaning.",
            },
          });
          return;
        }
        if (outcome.status === "conflict") {
          this.patchItem(item.itemId, {
            status: "conflict",
            error: { code: "invalid_response", message: outcome.message },
          });
          return;
        }
        const snapshot = this.vocabulary.getSnapshot();
        if (!snapshot) {
          this.patchItem(item.itemId, {
            status: "failed",
            error: { code: "storage_unavailable", message: "Vocabulary is not available." },
          });
          return;
        }
        const issues = validateMeaningPackage(outcome.package, selectedSense, snapshot);
        if (issues.length > 0) {
          this.patchItem(item.itemId, {
            status: "failed",
            error: {
              code: "invalid_response",
              message: issues[0]?.message ?? "The generated card could not be validated.",
            },
          });
          return;
        }
        this.patchItem(item.itemId, {
          status: "ready",
          pack: outcome.package,
          reviewNote: buildReviewNote(selectedSense, outcome.package),
          selectedForSave: true,
        });
      } catch (error) {
        if (token !== this.generationToken) {
          return;
        }
        this.patchItem(item.itemId, { status: "failed", error: this.describeError(error) });
      }
    });
    if (token !== this.generationToken) {
      return;
    }
    const latest = this.store.getSnapshot().draft ?? working;
    const ready = latest.items.filter((item) => item.status === "ready");
    const unfinished = latest.items.filter(
      (item) => item.status === "failed" || item.status === "conflict",
    );
    const firstFailure = unfinished.find((item) => item.error?.message)?.error;
    this.store.setState({
      phase: ready.length || unfinished.length ? "review" : "error",
      input: draft.input,
      existingWordId: draft.existingWordId,
      draft: latest,
      lookingForAnother: fromSingle,
      progressMessage: undefined,
      error: ready.length
        ? undefined
        : {
            code: firstFailure?.code ?? "generation_failed",
            message: firstFailure?.message ?? "None of the selected meanings could be generated.",
          },
    });
  }

  private patchItem(itemId: string, patch: Partial<DraftItem>): void {
    this.store.setState((current) => {
      if (!current.draft) {
        return current;
      }
      return {
        ...current,
        draft: {
          ...current.draft,
          items: current.draft.items.map((item) =>
            item.itemId === itemId ? { ...item, ...patch } : item,
          ),
        },
      };
    });
  }

  private async runPool<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
    const queue = [...items];
    const runners = Array.from({ length: Math.min(limit, Math.max(queue.length, 1)) }, async () => {
      while (queue.length) {
        const item = queue.shift();
        if (item) {
          await worker(item);
        }
      }
    });
    await Promise.all(runners);
  }

  private describeError(error: unknown): { code: string; message: string } {
    if (isProductError(error)) {
      return { code: error.code, message: error.message };
    }
    if (error instanceof DOMException && error.name === "AbortError") {
      return { code: "cancelled", message: "Generation was cancelled." };
    }
    return { code: "generation_failed", message: "Generation failed. Try again." };
  }
}

function hasRankingHints(input: AddWordInput): boolean {
  return Boolean(
    input.context?.trim() || input.definition?.trim() || input.russianHint?.trim() || input.typeHint,
  );
}

function mergeCandidates(
  existing: readonly MeaningCandidate[],
  incoming: readonly MeaningCandidate[],
): MeaningCandidate[] {
  const seen = new Set(existing.map((item) => item.id));
  return [...existing, ...incoming.filter((item) => !seen.has(item.id))];
}

function uniqueStrings(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}
